import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import RenderSelectedFilters from "./RenderSelectedFiltersProps";
import { fetchCohortFilterOptions } from "../../pages/assign-task/api";
import { useNavigate, useParams } from "react-router-dom";
import DropDownFilter from "../../pages/reports/RetailerAll/DropDownFilter";
import { useAlert } from "../../pages/AlertContext/AlertContext";
import { getToken } from "../../utils/utils";

interface Outlet {
  id: number | string;
  name: string;
}

interface FilterPopupProps {
  filter: any;
  defaultfiletrs: string[];
  selectedlocationstypes: string[];
  setSelectedlocationstypes: React.Dispatch<React.SetStateAction<string[]>>;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  cohort_type?: string;
  outletnames?: Outlet[];
}

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface Filters {
  outletTypes: { [key: string]: boolean };
  locations: { [key: string]: boolean };
  status: { [key: string]: boolean };
  Contract_since: { [key: string]: boolean };
  Current_rental_value: { [key: string]: boolean };
  asmNames: { [key: string]: boolean };
  territoryCodes: { [key: string]: boolean };
  taskNames: { [key: string]: boolean };
  region: { [key: string]: boolean };
  [key: string]: { [key: string]: boolean };
}

interface Task {
  id: string | number;
  name: string;
}

function FilterPopup({
  cohort_type,
  filters,
  setFilters,
  filter,
  defaultfiletrs,
  selectedlocationstypes,
  setSelectedlocationstypes,
  outletnames,
}: FilterPopupProps) {
  const { id } = useParams();
  const { showAlert } = useAlert();
  const [filtersInitialized, setFiltersInitialized] = useState({
    outletTypes: false,
    locations: false,
    status: false,
    Contract_since: false,
    Current_rental_value: false,
    asmNames: false,
    territoryCodes: false,
    taskNames: false,
    region: false,
  });

  const [asmNames, setAsmNames] = useState<Task[]>([]);
  const [territoryCodes, setTerritoryCodes] = useState<Task[]>([]);
  const [outletTypes, setOutletTypes] = useState<Task[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const navigate = useNavigate();
  const token = getToken();

  // Normalize case for consistency
  const normalizeCase = (str: string) =>
    str && typeof str === "string"
      ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
      : "";
const normalizeLocationCase = (str: string) =>
  str && typeof str === "string" ? str.toUpperCase() : "";
  // Normalize Contract_since values
  const normalizeContractSince = (range: string) => {
    if (!range || typeof range !== "string") return "";
    const normalized = range.toLowerCase().replace(/\s/g, "");
    const validRanges: { [key: string]: string } = {
      last1year: "Last1Year",
      last3year: "Last3Year",
      last6year: "Last6Year",
      above6year: "Above6Year",
      last1yr: "Last1Year",
      last3yr: "Last3Year",
      last6yr: "Last6Year",
      "1year": "Last1Year",
      "3year": "Last3Year",
      "6year": "Last6Year",
      above6yr: "Above6Year",
    };
    return validRanges[normalized] || range;
  };

  // Format Contract_since for UI labels
  const formatContractSinceLabel = (value: string) => {
    const mapping: Record<string, string> = {
      Last1Year: "Last 1 Year",
      Last3Year: "Last 3 Year",
      Last6Year: "Last 6 Year",
      Above6Year: "Above 6 Year",
    };
    return mapping[value] || value;
  };

  // Normalize outlet types
  const normalizeOutletType = (type: string) =>
    type
      .replace(/non\s*paid/i, "Nonpaid")
      .replace(/paid,\s*paid/i, "Paid")
      .replace(/non\s*paid,\s*non\s*paid/i, "Nonpaid")
      .replace(/non\s*paid,\s*paid/i, "Nonpaid,Paid")
      .replace(/paid,\s*non\s*paid/i, "Paid,Nonpaid");

  // Format Current_rental_value labels with ₹ symbol
  const formatRentalValueLabel = (range: string) => {
    if (range === "Above 6000") return "Above ₹6000";
    if (/^(\d+)-(\d+)$/.test(range)) {
      const [start, end] = range.split("-");
      return `₹${start}-₹${end}`;
    }
    return range;
  };

  // Debounce utility
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch filter options
  const fetchFilterOptions = useCallback(
    async (tabKey: string, updatedFilters: Filters) => {
      try {
        const selectedFilters = {
          locations: Object.entries(updatedFilters.locations)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          outlet_type: Object.entries(updatedFilters.outletTypes)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeOutletType(key)),
          region: Object.entries(updatedFilters.region)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          status: Object.entries(updatedFilters.status)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          Contract_since: Object.entries(updatedFilters.Contract_since)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeContractSince(key))
            .filter(Boolean),
          Current_rental_value: Object.entries(updatedFilters.Current_rental_value)
            .filter(([key, value]) => value)
            .map(([key]) => key),
          asm_names: Object.entries(updatedFilters.asmNames)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          territory_codes: Object.entries(updatedFilters.territoryCodes)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          outlet_names: tasks
            .filter((item) => updatedFilters.taskNames[item.id.toString()])
            .map((item) => item.id.toString()),
          cohortName: "",
          task_id: "",
        };

        console.log("Fetching filters for:", tabKey, "Payload:", JSON.stringify(selectedFilters, null, 2));

        const response = await fetchCohortFilterOptions(selectedFilters, token);
        console.log("API response:", JSON.stringify(response.data, null, 2));

        const data = response.data || {};

        const {
          locationsall = [],
          outlet_types = [],
          statusdata = [],
          regions = [],
          Current_rental_value_data = [],
          Contract_since_Data = [],
          outlet_names = [],
          asmnames = [],
          territoryCode = [],
        } = data;

        const validRentalValues = Current_rental_value_data.filter(
          (range: string) => /^(\d+)-(\d+)$/.test(range) || range === "Above 6000"
        );

        setFilters((prevFilters) => {
          const newFilters = { ...prevFilters };
          if (tabKey === "outletTypes" && Array.isArray(outlet_types)) {
            newFilters.outletTypes = outlet_types.reduce(
              (acc: { [key: string]: boolean }, type: string) => {
                const normalizedType = normalizeOutletType(type);
                acc[normalizedType] = prevFilters.outletTypes[normalizedType] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "locations" && Array.isArray(locationsall)) {
            newFilters.locations = locationsall.reduce(
              (acc: { [key: string]: boolean }, city: string) => {
                const normalizedCity = normalizeLocationCase(city);
                acc[normalizedCity] = prevFilters.locations[normalizedCity] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "region" && Array.isArray(regions)) {
            newFilters.region = regions.reduce(
              (acc: { [key: string]: boolean }, region: { id: string; label: string }) => {
                const normalizedRegion = normalizeCase(region.label);
                acc[normalizedRegion] = prevFilters.region[normalizedRegion] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "status" && Array.isArray(statusdata)) {
            newFilters.status = statusdata.reduce(
              (acc: { [key: string]: boolean }, status: string) => {
                const normalizedStatus = normalizeCase(status);
                acc[normalizedStatus] = prevFilters.status[normalizedStatus] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "Contract_since") {
            const validContractSinceData = Array.isArray(Contract_since_Data)
              ? Contract_since_Data.map((range: string) => normalizeContractSince(range)).filter(Boolean)
              : ["Last1Year", "Last3Year", "Last6Year", "Above6Year"];
            newFilters.Contract_since = validContractSinceData.reduce(
              (acc: { [key: string]: boolean }, range: string) => {
                acc[range] = prevFilters.Contract_since[range] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "Current_rental_value" && Array.isArray(validRentalValues)) {
            newFilters.Current_rental_value = validRentalValues.reduce(
              (acc: { [key: string]: boolean }, range: string) => {
                acc[range] = prevFilters.Current_rental_value[range] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "taskNames") {
            const combinedOutletNames = [
              ...(Array.isArray(outletnames)
                ? outletnames.filter(
                  (item): item is { id: string | number; name: string } =>
                    !!item && !!item.name && typeof item.name === "string"
                )
                : []),
              ...(Array.isArray(outlet_names)
                ? outlet_names.filter(
                  (item): item is { id: string | number; name: string } =>
                    !!item &&
                    !!item.name &&
                    typeof item.name === "string" &&
                    !(outletnames ?? []).some(
                      (existing) => existing.id.toString() === item.id.toString()
                    )
                )
                : []),
            ];
            newFilters.taskNames = combinedOutletNames.reduce(
              (acc: { [key: string]: boolean }, item: { id: string | number; name: string }) => {
                const taskId = item.id.toString();
                acc[taskId] = updatedFilters.taskNames[taskId] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "asmNames" && Array.isArray(asmnames)) {
            newFilters.asmNames = asmnames.reduce(
              (acc: { [key: string]: boolean }, asm: { id: string; name: string }) => {
                const normalizedAsm = normalizeCase(asm.name);
                acc[normalizedAsm] = prevFilters.asmNames[normalizedAsm] ?? false;
                return acc;
              },
              {}
            );
          }
          if (tabKey === "territoryCodes" && Array.isArray(territoryCode)) {
            newFilters.territoryCodes = territoryCode.reduce(
              (acc: { [key: string]: boolean }, territory: { id: string; name: string }) => {
                const normalizedTerritory = normalizeCase(territory.name);
                acc[normalizedTerritory] = prevFilters.territoryCodes[normalizedTerritory] ?? false;
                return acc;
              },
              {}
            );
          }
          return newFilters;
        });

        setTasks((prevTasks) => {
          const combinedOutletNames = [
            ...(Array.isArray(outletnames)
              ? outletnames.filter(
                (item): item is { id: string | number; name: string } =>
                  !!item && !!item.name && typeof item.name === "string"
              )
              : []),
            ...(Array.isArray(outlet_names)
              ? outlet_names.filter(
                (item): item is { id: string | number; name: string } =>
                  !!item &&
                  !!item.name &&
                  typeof item.name === "string" &&
                  !(outletnames ?? []).some(
                    (existing) => existing.id.toString() === item.id.toString()
                  )
              )
              : []),
          ];
          return combinedOutletNames.map((item) => ({
            id: item.id,
            name: normalizeCase(item.name),
          }));
        });

        setAsmNames(
          Array.isArray(asmnames)
            ? asmnames
              .filter(
                (asm): asm is { id: string; name: string } =>
                  !!asm && !!asm.name && typeof asm.name === "string"
              )
              .map((asm) => ({
                id: normalizeCase(asm.name),
                name: normalizeCase(asm.name),
              }))
            : asmNames
        );
        setTerritoryCodes(
          Array.isArray(territoryCode)
            ? territoryCode
              .filter(
                (territory): territory is { id: string; name: string } =>
                  !!territory && !!territory.name && typeof territory.name === "string"
              )
              .map((territory) => ({
                id: normalizeCase(territory.name),
                name: normalizeCase(territory.name),
              }))
            : territoryCodes
        );
        setOutletTypes(
          Array.isArray(outlet_types)
            ? outlet_types
              .filter((type): type is string => !!type && typeof type === "string")
              .map((type) => ({
                id: normalizeOutletType(type),
                name: normalizeOutletType(type),
              }))
            : outletTypes
        );

        if (Current_rental_value_data.some((range: string) => !/^\d+-\d+$/.test(range) && range !== "Above 6000")) {
          showAlert("Invalid rental value range detected. Please check the filter options.", "warning");
        }
      } catch (error) {
        console.error(`Error fetching filter options for ${tabKey}:`, error);
        showAlert(
          `Failed to load ${tabsConfig.find((tab) => tab.key === tabKey)?.label || tabKey} options.`,
          "error"
        );
      }
    },
    [token, setFilters, showAlert, outletnames, tasks, asmNames, territoryCodes, outletTypes]
  );

  const debouncedFetchFilterOptions = useCallback(debounce(fetchFilterOptions, 300), [
    fetchFilterOptions,
  ]);
  console.log(filtersInitialized, "filtersInitialized22222")
  console.log(defaultfiletrs, "defaultfiletrsdefaultfiletrs")
  useEffect(() => {
    const initializeFilters = async () => {
      try {
        const defaultFiltersArray = Array.isArray(defaultfiletrs)
          ? defaultfiletrs
          : JSON.parse(defaultfiletrs || "[]");

        const response = await fetchCohortFilterOptions(
          {
            // locations: defaultFiltersArray
            //   .filter((f: string) =>
            //     Object.keys(filters.locations).some((key) => normalizeCase(key) === normalizeCase(f))
            //   )
            //   .map(normalizeCase),
            outlet_type: defaultFiltersArray
              .filter((f: string) =>
                outletTypes.some((type) => normalizeOutletType(type.name) === normalizeOutletType(f))
              )
              .map(normalizeOutletType),
            region: defaultFiltersArray
              .filter((f: string) =>
                Object.keys(filters.region).some((key) => normalizeCase(key) === normalizeCase(f))
              )
              .map(normalizeCase),
            status: defaultFiltersArray
              .filter((f: string) =>
                Object.keys(filters.status).some((key) => normalizeCase(key) === normalizeCase(f))
              )
              .map(normalizeCase),
            Contract_since: defaultFiltersArray
              .filter((f: string) =>
                Object.keys(filters.Contract_since).some(
                  (key) => normalizeContractSince(key) === normalizeContractSince(f)
                )
              )
              .map(normalizeContractSince)
              .filter(Boolean),
            Current_rental_value: defaultFiltersArray
              .filter((f: string) =>
                Object.keys(filters.Current_rental_value).some((key) => key === f)
              )
              .map((f: string) => f),
            asm_names: defaultFiltersArray
              .filter((f: string) =>
                asmNames.some((asm) => normalizeCase(asm.name) === normalizeCase(f))
              )
              .map(normalizeCase),
            territory_codes: defaultFiltersArray
              .filter((f: string) =>
                territoryCodes.some((territory) => normalizeCase(territory.name) === normalizeCase(f))
              )
              .map(normalizeCase),
            outlet_names: Array.isArray(outletnames)
              ? outletnames
                .filter(
                  (item): item is { id: string | number; name: string } =>
                    !!item && !!item.name && typeof item.name === "string"
                )
                .map((item) => item.id.toString())
              : [],
            cohortName: "",
            task_id: "",
          },
          token
        );

        console.log("Initial API response:", JSON.stringify(response.data, null, 2));

        const data = response.data || {};

        const {
          asmnames = [],
          territoryCode = [],
          outlet_types = [],
          locationsall = [],
          regions = [],
          statusdata = [],
          Contract_since_Data = [],
          Current_rental_value_data = [],
          outlet_names = [],
        } = data;



        setFilters((prevFilters) => ({
          outletTypes: Array.isArray(outlet_types)
            ? outlet_types.reduce((acc: { [key: string]: boolean }, type: string) => {
              const normalizedType = normalizeOutletType(type);
              acc[normalizedType] = defaultFiltersArray.includes(normalizedType);
              return acc;
            }, {})
            : {},
          locations: Array.isArray(locationsall)
            ? locationsall.reduce((acc: { [key: string]: boolean }, city: string) => {
              const normalizedCity = normalizeLocationCase(city);
              acc[normalizedCity] = defaultFiltersArray.includes(normalizedCity);
              return acc;
            }, {})
            : {},
          region: Array.isArray(regions)
            ? regions.reduce((acc: { [key: string]: boolean }, region: { id: string; label: string }) => {
              const normalizedRegion = normalizeCase(region.label);
              acc[normalizedRegion] = defaultFiltersArray.includes(normalizedRegion);
              return acc;
            }, {})
            : {},
          status: Array.isArray(statusdata)
            ? statusdata.reduce((acc: { [key: string]: boolean }, status: string) => {
              const normalizedStatus = normalizeCase(status);
              acc[normalizedStatus] = defaultFiltersArray.includes(normalizedStatus);
              return acc;
            }, {})
            : { Active: false, Inactive: false },
          Contract_since: Array.isArray(Contract_since_Data)
            ? Contract_since_Data.map((range: string) => normalizeContractSince(range))
              .filter(Boolean)
              .reduce((acc: { [key: string]: boolean }, range: string) => {
                acc[range] = defaultFiltersArray.includes(range);
                return acc;
              }, {})
            : { Last1Year: false, Last3Year: false, Last6Year: false, Above6Year: false },
          Current_rental_value: Array.isArray(Current_rental_value_data)
            ? Current_rental_value_data
              .filter((range: string) => /^(\d+)-(\d+)$/.test(range) || range === "Above 6000")
              .reduce((acc: { [key: string]: boolean }, range: string) => {
                acc[range] = defaultFiltersArray.includes(range);
                return acc;
              }, {})
            : { "0-2000": false, "2000-40000": false, "4000-50000": false, "Above 6000": false },
          asmNames: Array.isArray(asmnames)
            ? asmnames.reduce((acc: { [key: string]: boolean }, asm: { id: string; name: string }) => {
              const normalizedAsm = normalizeCase(asm.name);
              acc[normalizedAsm] = defaultFiltersArray.includes(normalizedAsm);
              return acc;
            }, {})
            : {},
          territoryCodes: Array.isArray(territoryCode)
            ? territoryCode.reduce(
              (acc: { [key: string]: boolean }, territory: { id: string; name: string }) => {
                const normalizedTerritory = normalizeCase(territory.name);
                acc[normalizedTerritory] = defaultFiltersArray.includes(normalizedTerritory);
                return acc;
              },
              {}
            )
            : {},
          taskNames: Array.isArray(outletnames)
            ? outletnames
              .filter(
                (item): item is { id: string | number; name: string } =>
                  !!item && !!item.name && typeof item.name === "string"
              )
              .reduce((acc: { [key: string]: boolean }, item: { id: string | number; name: string }) => {
                acc[item.id.toString()] = true; // Preselect all outletnames
                return acc;
              }, {})
            : {},
        }));

        setTasks(
          Array.isArray(outletnames)
            ? outletnames
              .filter(
                (item): item is { id: string | number; name: string } =>
                  !!item && !!item.name && typeof item.name === "string"
              )
              .map((item) => ({ id: item.id, name: normalizeCase(item.name) }))
            : []
        );
        setAsmNames(
          Array.isArray(asmnames)
            ? asmnames
              .filter(
                (asm): asm is { id: string; name: string } =>
                  !!asm && !!asm.name && typeof asm.name === "string"
              )
              .map((asm) => ({
                id: normalizeCase(asm.name),
                name: normalizeCase(asm.name),
              }))
            : []
        );
        setTerritoryCodes(
          Array.isArray(territoryCode)
            ? territoryCode
              .filter(
                (territory): territory is { id: string; name: string } =>
                  !!territory && !!territory.name && typeof territory.name === "string"
              )
              .map((territory) => ({
                id: normalizeCase(territory.name),
                name: normalizeCase(territory.name),
              }))
            : []
        );
        setOutletTypes(
          Array.isArray(outlet_types)
            ? outlet_types
              .filter((type): type is string => !!type && typeof type === "string")
              .map((type) => ({
                id: normalizeOutletType(type),
                name: normalizeOutletType(type),
              }))
            : []
        );

        setSelectedlocationstypes(
          Array.isArray(locationsall)
            ? locationsall
              .filter((city: string) => defaultFiltersArray.includes(normalizeCase(city)))
              .map((city: string) => normalizeCase(city))
            : []
        );

        setFiltersInitialized({
          outletTypes: true,
          locations: true,
          status: true,
          Contract_since: true,
          Current_rental_value: true,
          asmNames: true,
          territoryCodes: true,
          taskNames: true,
          region: true,
        });
      } catch (error) {
        console.error("Error initializing filters:", error);
        showAlert("Failed to initialize filter options.", "error");
      }
    };

    initializeFilters();
  }, [token, setFilters, showAlert, defaultfiletrs, setSelectedlocationstypes, outletnames]);

  const [hasFetchedFilters, setHasFetchedFilters] = useState(false);

  useEffect(() => {
    if (
      !hasFetchedFilters &&
      (tasks.length > 0 || asmNames.length > 0 || territoryCodes.length > 0 || outletTypes.length > 0)
    ) {
      debouncedFetchFilterOptions("locations", filters);
      setHasFetchedFilters(true);
    }
  }, [tasks, asmNames, territoryCodes, outletTypes, filters, debouncedFetchFilterOptions, hasFetchedFilters]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const tabKey = tabsConfig[newValue].key;
    debouncedFetchFilterOptions(tabKey, filters);
  };

  const handleFilterChange = (tabKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const normalizedName =
      // tabKey === "Contract_since" ? normalizeContractSince(name) : normalizeCase(name);

      tabKey === "Contract_since"
    ? normalizeContractSince(name)
    : tabKey === "locations"
    ? normalizeLocationCase(name) // Use new function for locations
    : normalizeCase(name);


    if (tabKey === "Contract_since" && !normalizedName) {
      console.warn(`Skipping invalid Contract_since value: ${name}`);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        [normalizedName]: checked,
      },
    }));
    if (tabKey === "locations") {
      setSelectedlocationstypes((prev) =>
        checked ? [...prev, normalizedName] : prev.filter((loc) => loc !== normalizedName)
      );
    }
    debouncedFetchFilterOptions(tabKey, {
      ...filters,
      [tabKey]: {
        ...filters[tabKey],
        [normalizedName]: checked,
      },
    });
  };

  const handleSelectAll = (tabKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [tabKey]: Object.keys(prevFilters[tabKey]).reduce((acc, key) => {
          const normalizedKey =
            tabKey === "Contract_since" ? normalizeContractSince(key) : normalizeCase(key);
          if (tabKey === "Contract_since" && !normalizedKey) return acc;
          acc[normalizedKey] = checked;
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      debouncedFetchFilterOptions(tabKey, updatedFilters);
      if (tabKey === "locations") {
        setSelectedlocationstypes(
          checked ? Object.keys(prevFilters[tabKey]).map(normalizeCase) : []
        );
      }
      return updatedFilters;
    });
  };

  const handleTaskSelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        taskNames: tasks.reduce((acc, task) => {
          const taskId = task.id.toString();
          acc[taskId] = selected.includes(taskId);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      console.log("handleTaskSelection:", { selected, updatedFilters });
      debouncedFetchFilterOptions("taskNames", updatedFilters);
      return updatedFilters;
    });
  };

  const handleAsmSelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        asmNames: Object.keys(prevFilters.asmNames).reduce((acc, asm) => {
          acc[asm] = selected.includes(asm);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      debouncedFetchFilterOptions("asmNames", updatedFilters);
      return updatedFilters;
    });
  };

  const handleTerritorySelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        territoryCodes: Object.keys(prevFilters.territoryCodes).reduce((acc, territory) => {
          acc[territory] = selected.includes(territory);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      debouncedFetchFilterOptions("territoryCodes", updatedFilters);
      return updatedFilters;
    });
  };

  const handleOutletTypeSelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        outletTypes: Object.keys(prevFilters.outletTypes).reduce((acc, type) => {
          acc[type] = selected.includes(type);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      debouncedFetchFilterOptions("outletTypes", updatedFilters);
      return updatedFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        outletTypes: Object.fromEntries(
          Object.keys(prevFilters.outletTypes).map((key) => [normalizeOutletType(key), false])
        ),
        locations: Object.fromEntries(
          Object.keys(prevFilters.locations).map((key) => [normalizeCase(key), false])
        ),
        region: Object.fromEntries(
          Object.keys(prevFilters.region).map((key) => [normalizeCase(key), false])
        ),
        status: Object.fromEntries(
          Object.keys(prevFilters.status).map((key) => [normalizeCase(key), false])
        ),
        Contract_since: Object.fromEntries(
          ["Last1Year", "Last3Year", "Last6Year", "Above6Year"].map((key) => [key, false])
        ),
        Current_rental_value: Object.fromEntries(
          Object.keys(prevFilters.Current_rental_value).map((key) => [key, false])
        ),
        asmNames: Object.fromEntries(
          Object.keys(prevFilters.asmNames).map((key) => [normalizeCase(key), false])
        ),
        territoryCodes: Object.fromEntries(
          Object.keys(prevFilters.territoryCodes).map((key) => [normalizeCase(key), false])
        ),
        taskNames: Object.fromEntries(
          Object.keys(prevFilters.taskNames).map((key) => [key, false])
        ),
      };
      debouncedFetchFilterOptions("all", updatedFilters);
      setSelectedlocationstypes([]);
      return updatedFilters;
    });
  };

  const taskOptions: Option[] = (tasks || []).map((task) => ({
    label: task.name,
    value: task.id.toString(),
  }));
  const asmOptions: Option[] = asmNames.map((asm) => ({
    label: asm.name,
    value: asm.name,
  }));
  const territoryOptions: Option[] = territoryCodes.map((territory) => ({
    label: territory.name,
    value: territory.name,
  }));
  const outletTypeOptions: Option[] = outletTypes.map((type) => ({
    label: type.name,
    value: type.name,
  }));

  const tabsConfig = [
    { label: "Outlet Types", key: "outletTypes" },
    { label: "Location", key: "locations" },
    { label: "Status", key: "status" },
    { label: "Contract Since", key: "Contract_since" },
    { label: "Current Rental Value", key: "Current_rental_value" },
    { label: "Region", key: "region" },
  ];

  const handleDelete = (tabKey: string, value: string) => {
    setFilters((prevFilters) => {
      const normalizedValue =
        tabKey === 'Contract_since'
          ? normalizeContractSince(value)
          : tabKey === 'outletTypes'
            ? normalizeOutletType(value)
            : tabKey === 'taskNames'
              ? value // Use raw value (ID) for taskNames
              : normalizeCase(value);

      const updatedFilters = {
        ...prevFilters,
        [tabKey]: {
          ...prevFilters[tabKey],
          [normalizedValue]: false,
        },
      };

      // Update selectedlocationstypes for locations
      if (tabKey === 'locations') {
        setSelectedlocationstypes((prev) =>
          prev.filter((loc) => normalizeCase(loc) !== normalizeCase(value))
        );
      }

      console.log(
        `Deleted filter: ${value} from ${tabKey}`,
        'Updated filters:',
        JSON.stringify(updatedFilters, null, 2)
      );

      debouncedFetchFilterOptions(tabKey, updatedFilters);
      return updatedFilters;
    });
  };
  console.log(defaultfiletrs, "777777733321")

  // Determine if "Select All" should be shown
  const shouldShowSelectAll = (tabKey: string) => {
    return Object.keys(filters[tabKey]).length >= 2;
  };

  const shouldShowSelectAllForDropdown = (options: Option[]) => {
    return options.length >= 2;
  };


  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          right: "3%",
          backgroundColor: "#fff",
          padding: "20px 0",
          borderRadius: "10px",
          display: "inherit !important",
        }}
      >
        {cohort_type === "Static Cohort" && (
          <>
            <Box
              sx={{
                minWidth: "100%",
                width: "98%",
                color: "#DEDEDE",
                background: "#fff !important",
                position: "relative",
                top: "-23px",
                borderRadius: "8px",
                fontWeight: "500",
                justifyContent: "center",
                backgroundColor: "#FFF",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mt: 2, color: "#282829", fontSize: "16px", mb: 1 }}
              >
                Select Outlet Types
              </Typography>
              <DropDownFilter
                options={outletTypeOptions}
                value={Object.keys(filters.outletTypes).filter((key) => filters.outletTypes[key])}
                onChange={handleOutletTypeSelection}
                placeholder="Select Outlet Types"
                tabKey="outletTypes"
                maxMenuHeight={300}
                showSelectAll={shouldShowSelectAllForDropdown(outletTypeOptions)}
              />
            </Box>
            <Box
              sx={{
                minWidth: "100%",
                width: "98%",
                color: "#DEDEDE",
                background: "#fff !important",
                position: "relative",
                top: "-23px",
                borderRadius: "8px",
                fontWeight: "500",
                justifyContent: "center",
                backgroundColor: "#FFF",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mt: 2, color: "#282829", fontSize: "16px", mb: 1 }}
              >
                Select Outlet Names
              </Typography>
              <DropDownFilter
                options={taskOptions}
                value={Object.keys(filters.taskNames).filter((key) => filters.taskNames[key])}
                onChange={handleTaskSelection}
                placeholder="Select Outlet Names"
                tabKey="taskNames"
                maxMenuHeight={300}
                showSelectAll={shouldShowSelectAllForDropdown(taskOptions)}
              />
            </Box>
            <Box
              sx={{
                minWidth: "100%",
                width: "98%",
                color: "#DEDEDE",
                background: "#fff !important",
                position: "relative",
                top: "-23px",
                borderRadius: "8px",
                fontWeight: "500",
                justifyContent: "center",
                backgroundColor: "#FFF",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mt: 2, color: "#282829", fontSize: "16px", mb: 1 }}
              >
                Select ASM Names
              </Typography>
              <DropDownFilter
                options={asmOptions}
                value={Object.keys(filters.asmNames).filter((key) => filters.asmNames[key])}
                onChange={handleAsmSelection}
                placeholder="Select ASM Names"
                tabKey="asmNames"
                maxMenuHeight={300}
                showSelectAll={shouldShowSelectAllForDropdown(asmOptions)}
              />
            </Box>
            <Box
              sx={{
                minWidth: "100%",
                width: "98%",
                color: "#DEDEDE",
                background: "#fff !important",
                position: "relative",
                top: "-23px",
                borderRadius: "8px",
                fontWeight: "500",
                justifyContent: "center",
                backgroundColor: "#FFF",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mt: 2, color: "#282829", fontSize: "16px", mb: 1 }}
              >
                Select Territory Code
              </Typography>
              <DropDownFilter
                options={territoryOptions}
                value={Object.keys(filters.territoryCodes).filter((key) => filters.territoryCodes[key])}
                onChange={handleTerritorySelection}
                placeholder="Select Territory Code"
                tabKey="territoryCodes"
                maxMenuHeight={300}
                showSelectAll={shouldShowSelectAllForDropdown(territoryOptions)}
              />
            </Box>

          </>
        )}

        {cohort_type === "Dynamic Cohort" && (
          <Box sx={{ display: "flex", width: "100%" }}>
            <Box sx={{ width: "200px", borderRight: "1px solid #ddd" }}>
              <Tabs
                orientation="vertical"
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="Filter tabs"
                sx={{
                  borderRight: 1,
                  borderColor: "divider",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  color: "#282829",
                  fontWeight: "500",
                  textAlign: "left",
                }}
              >
                {tabsConfig.map((tab, index) => (
                  <Tab key={tab.key} label={tab.label} sx={tabStyles} />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ paddingLeft: "20px", flexGrow: 1 }}>
              {/* <Button
                variant="outlined"
                onClick={clearAllFilters}
                sx={{
                 
                  textTransform: "none",
                  color: "#C8172F",
                  borderColor: "#C8172F",
                  marginBottom: "-825px",
                  marginLeft: "-133px",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#C8172F",
                  },
                }}
              >
                Clear All Filters
              </Button> */}
              {tabsConfig.map((tab, index) =>
                selectedTab === index ? (
                  <Box key={tab.key} sx={{ marginBottom: "16px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Object.values(filters[tab.key]).every(
                            (value) => value
                          )}
                          onChange={(event) => handleSelectAll(tab.key, event)}
                          name="selectAll"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "14px" }}>
                          Select All
                        </Typography>
                      }
                      sx={{ marginBottom: "1px", display: "flex" }}
                    />
                    {filters[tab.key] &&
                      Object.keys(filters[tab.key]).map((filterKey) => (
                        <FormControlLabel
                          key={filterKey}
                          sx={{ display: "flex" }}
                          control={
                            <Checkbox
                              checked={filters[tab.key][filterKey] || false}
                              onChange={(event) => handleFilterChange(tab.key, event)}
                              name={filterKey}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: "14px" }}>
                              {capitalize(filterKey)}
                            </Typography>
                          }
                        />
                      ))}
                  </Box>
                ) : null
              )}
            </Box>
          </Box>
        )}
      </Box>

      {defaultfiletrs?.length > 0 && (
        <Box sx={{ marginTop: "20px" }}>
          <RenderSelectedFilters
            filters={filters}
            tasks={tasks}
            maxVisible={3}
            showAllInitially={false}
            showTitle={true}
            title="Applied Filters"
            showCloseBtn={true}
            onDelete={handleDelete}
            formatRentalValueLabel={formatRentalValueLabel}
            formatContractSinceLabel={formatContractSinceLabel}
          />
        </Box>
      )}
           {defaultfiletrs?.length > 0 && (
      <Button
        variant="outlined"
        onClick={clearAllFilters}
        sx={{
          marginTop: "20px",
          textTransform: "none",
          color: "#C8172F",
          borderColor: "#C8172F",
          position: "relative",
          bottom: "0px",
          left: (defaultfiletrs?.length ?? 0) === 0 ? "100px" : "88px",
          height: "42px",
          borderRadius: "10px",
          marginBottom: "-90px",
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#C8172F",
          },
        }}
      >
        Clear All Filters
      </Button>
         )}
    </Box>
  );
}

const tabStyles = {
  whiteSpace: "nowrap",
  fontSize: "14px",
  color: "#282829",
  fontWeight: "500",
  textTransform: "capitalize",
  alignItems: "start",
  "&:hover": { backgroundColor: "#EEF4FF" },
  "&.Mui-selected": { backgroundColor: "#EEF4FF", color: "#000" },
};

const capitalize = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

export default FilterPopup;